import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { featureFlag } from 'app/util/featureFlag';
import { useParams } from 'react-router';

import { useGetAppConfigQuery } from 'app/api/mainApi';

import {
  useGetDirectMessagesMailboxQuery,
  useGetSentMessagesMailboxQuery,
  useGetSystemMessagesMailboxQuery,
} from 'app/features/mail/api/mailApi';
import { useMessageDetails } from 'app/features/mail/hooks/useMessageDetails';

import { styled } from '@mui/material/styles';
import {
  Grid,
  Stack,
  useMediaQuery,
} from '@mui/material';

import Mailboxes from 'app/features/mail/components/Mailboxes';
import MailboxPagination from 'app/features/mail/components/MailboxPagination';
import TitleHeader from 'app/components/shared/TitleHeader';
import MessageDetails from 'app/features/mail/components/MessageDetails';

const HEADER_NAV_HEIGHT = 44;
const HEADER_HEIGHT = 44;
const MOBILE_HEADER_HEIGHT = 36;
const MOBILE_HEADER_NAV_HEIGHT = 36;
const MAILBOX_TABS_HEIGHT = 48;
const PAGINATION_CONTROLS_HEIGHT = 48;
const FOOTER_HEIGHT = 55;
const MARGIN_OF_ERROR = 2;

const MailBodyStack = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'isAndroid',
})(({ isAndroid }) => ({
  direction: 'column',
  spacing: 0,
  height: 'calc(100vh - 70px - var(--banner-height, 0px))',
  '@media (max-width: 900px)': {
    height: 'calc(100vh - 160px - var(--banner-height, 0px))',
  },
  '@media (max-width: 600px)': {
    height: isAndroid
      ? 'calc(100vh - 170px - var(--banner-height, 0px))'
      : 'calc(100vh - 225px - var(--banner-height, 0px))',
  },
}));

function getMailboxQueryHook(tab) {
  switch (tab) {
  case 'inbox':
    return useGetDirectMessagesMailboxQuery;
  case 'notifications':
    return useGetSystemMessagesMailboxQuery;
  case 'sent':
    return useGetSentMessagesMailboxQuery;
  default:
    return useGetDirectMessagesMailboxQuery;
  }
}

function computeMailboxMaxHeight({
  mobileMailboxHeaderAndFooterHeight,
  mailboxHeaderAndFooterHeight,
}) {
  return {
    xs: `calc(100vh - ${mobileMailboxHeaderAndFooterHeight}px - var(--banner-height, 0px))`,
    md: `calc(100vh - ${mailboxHeaderAndFooterHeight}px - var(--banner-height, 0px))`,
  };
}

function computeMessageDetailsMaxHeight({
  mobileDetailsMaxHeight,
  messageDetailHeaderAndFooterHeight,
}) {
  return {
    xs: `calc(100vh - ${mobileDetailsMaxHeight}px - var(--banner-height, 0px))`,
    md: `calc(100vh - ${messageDetailHeaderAndFooterHeight}px - var(--banner-height, 0px))`,
  };
}

const MailboxesContainer = () => {
  const { tabName } = useParams();
  const [selectedTab, setSelectedTab] = useState(tabName);
  const selectTab = (tabName) => {
    if (tabName !== selectedTab) {
      window.history.pushState(null, null, `/messages/${tabName}`);
      setSelectedTab(tabName);
    }
  };
  const [selectedMessage, setSelectedMessage] = useState(null);

  const [searchParams] = useSearchParams();
  const pageFromParams = searchParams.get('page');
  const [page, setPage] = useState((pageFromParams && parseInt(pageFromParams)) || 1);

  const { data: appConfig } = useGetAppConfigQuery();

  const navigate = useNavigate();
  const mailboxQueryHook = getMailboxQueryHook(selectedTab);

  if (appConfig && !featureFlag.enabled('MAIL_21578')) {
    window.location.href = '/';
  }

  useEffect(() => {
    const originalBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = 'var(--body_background)';
    return () => {
      document.body.style.backgroundColor = originalBg;
    };
  }, []);

  useEffect(() => {
    if (!['inbox', 'notifications', 'sent'].includes(selectedTab)) {
      setSelectedTab('inbox');
      navigate('/messages/inbox');
    }
  }, [selectedTab, navigate]);

  const {
    data: mailbox,
    isLoading: isMailboxLoading,
  } = mailboxQueryHook({ page });

  // There has to be a better way to get the inner content to scroll.
  // It would be so nice to make the document just fall into place instead of arm wrestling it.
  // Header, Tabs, and 2 for margin of error.
  const hasMultiplePages = mailbox && mailbox.pages > 1;
  const mailboxHeaderAndFooterHeight = HEADER_NAV_HEIGHT + HEADER_HEIGHT + MAILBOX_TABS_HEIGHT + MARGIN_OF_ERROR + (hasMultiplePages ? PAGINATION_CONTROLS_HEIGHT : 0);
  const messageDetailHeaderAndFooterHeight = HEADER_NAV_HEIGHT + HEADER_HEIGHT + MARGIN_OF_ERROR;
  const mobileMailboxHeaderAndFooterHeight = MOBILE_HEADER_NAV_HEIGHT + MOBILE_HEADER_HEIGHT + MAILBOX_TABS_HEIGHT + FOOTER_HEIGHT + MARGIN_OF_ERROR + (hasMultiplePages ? PAGINATION_CONTROLS_HEIGHT : 0);
  const mobileDetailsMaxHeight = MOBILE_HEADER_NAV_HEIGHT + MOBILE_HEADER_HEIGHT + FOOTER_HEIGHT + MARGIN_OF_ERROR;

  const mailboxMaxHeight = computeMailboxMaxHeight({
    mobileMailboxHeaderAndFooterHeight,
    mailboxHeaderAndFooterHeight,
  });

  const messageDetailsMaxHeight = computeMessageDetailsMaxHeight({
    mobileDetailsMaxHeight,
    messageDetailHeaderAndFooterHeight,
  });

  const isSmallerThanMdScreen = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const isAndroid = /Android/i.test(navigator.userAgent);

  // Use custom hook for message details logic
  const {
    selectedMessageDetails,
    setSelectedMessageDetails,
    isLoading: isMessageDetailsLoading,
    closeMessageDetails,
    fetchReceivedMessageDetails,
  } = useMessageDetails({ selectedMessage, setSelectedMessage, selectedTab });

  useEffect(() => {
    closeMessageDetails();
  }, [selectedTab]);

  return (
    <Grid
      container
      direction="row"
      spacing={0}
      alignContent="flex-start"
    >
      <Grid
        item
        xs={12}
        // The Title Header Component has a negative margin bottom of 10 for some reason.
        marginBottom="10px"
      >
        <TitleHeader title="Mail" />
      </Grid>
      { (isSmallerThanMdScreen && selectedMessage) ? null : (
        <Grid
          item
          xs={12}
          sm={selectedMessage === null ? 12 : 5}
          sx={{
            xs: { display: selectedMessage === null ? 'flex' : 'none' },
            borderRight: '1px solid var(--color_ddd)',
          }}
        >
          <MailBodyStack isAndroid={isAndroid}>
            <Mailboxes
              isLoading={isMailboxLoading}
              mailbox={mailbox}
              selectedTab={selectedTab}
              selectTab={selectTab}
              currentUser={appConfig.current_user}
              setPage={setPage}
              selectedMessage={selectedMessage}
              setSelectedMessage={setSelectedMessage}
              maxHeight={mailboxMaxHeight}
            />
            { mailbox && mailbox.pages > 1 && (
              <MailboxPagination
                mailbox={mailbox}
                page={page}
                setPage={setPage}
              />
            )}
          </MailBodyStack>
        </Grid>
      )}
      { selectedMessage !== null && (
        <Grid
          item
          xs={12}
          md={7}
          onClose={() => setSelectedMessage(null)}
          sx={{
            backgroundColor: 'var(--body_background)',
          }}
        >
          <MessageDetails
            fetchReceivedMessageDetails={fetchReceivedMessageDetails}
            messageDetails={selectedMessageDetails}
            currentUser={appConfig.current_user}
            selectedTab={selectedTab}
            isLoading={isMessageDetailsLoading}
            closeFunc={closeMessageDetails}
            maxHeight={messageDetailsMaxHeight}
            setSelectedMessageDetails={setSelectedMessageDetails}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default MailboxesContainer;
